import * as React from 'react'

import { cn } from './cn'

export interface ChipProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'color'> {
  label?: React.ReactNode
  icon?: React.ReactElement
  avatar?: React.ReactElement
  onDelete?: React.MouseEventHandler<HTMLButtonElement>
  deleteIcon?: React.ReactElement
  color?:
    | 'default'
    | 'primary'
    | 'secondary'
    | 'error'
    | 'warning'
    | 'info'
    | 'success'
  variant?: 'filled' | 'outlined'
  size?: 'small' | 'medium'
  clickable?: boolean
  disabled?: boolean
  component?: React.ElementType
  href?: string
  target?: string
}

/**
 * Soft pill chips: tinted backgrounds with readable foregrounds rather
 * than the old solid Material fills.
 */
const colorClasses: Record<
  NonNullable<ChipProps['variant']>,
  Record<NonNullable<ChipProps['color']>, string>
> = {
  filled: {
    default: 'bg-surface text-text-primary border border-divider',
    primary: 'bg-primary/[0.14] text-primary border border-primary/20',
    secondary: 'bg-primary/[0.14] text-primary border border-primary/20',
    error:
      'bg-error/[0.12] text-error-dark dark:text-error-light border border-error/20',
    warning:
      'bg-warning/[0.14] text-warning-dark dark:text-warning-light border border-warning/25',
    info: 'bg-info/[0.12] text-info-dark dark:text-info-light border border-info/20',
    success:
      'bg-success/[0.12] text-success-dark dark:text-success-light border border-success/20',
  },
  outlined: {
    default: 'border border-divider-strong text-text-primary',
    primary: 'border border-primary/40 text-primary',
    secondary: 'border border-primary/40 text-primary',
    error: 'border border-error/40 text-error',
    warning: 'border border-warning/50 text-warning-dark dark:text-warning',
    info: 'border border-info/40 text-info',
    success: 'border border-success/40 text-success',
  },
}

const Chip = React.forwardRef<HTMLDivElement, ChipProps>(
  (
    {
      label,
      icon,
      avatar,
      onDelete,
      deleteIcon,
      color = 'default',
      variant = 'filled',
      size = 'medium',
      clickable,
      disabled,
      className,
      component,
      onClick,
      children,
      ...props
    },
    ref,
  ) => {
    const interactive = clickable || Boolean(onClick)
    const Component: React.ElementType =
      component || (interactive ? 'button' : 'div')

    return (
      <Component
        ref={ref}
        type={Component === 'button' ? 'button' : undefined}
        onClick={onClick}
        className={cn(
          'inline-flex max-w-full items-center justify-center gap-1.5 rounded-full whitespace-nowrap align-middle font-medium',
          size === 'small' ? 'h-6 px-2.5 text-xs' : 'h-8 px-3.5 text-sm',
          colorClasses[variant][color],
          interactive &&
            'cursor-pointer transition-all duration-150 active:scale-[0.97] hover:brightness-[0.97] dark:hover:brightness-125 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
          disabled && 'pointer-events-none opacity-40',
          className,
        )}
        {...props}
      >
        {avatar && <span className="-ml-1.5 flex shrink-0">{avatar}</span>}
        {icon && <span className="-ml-1 flex shrink-0">{icon}</span>}
        <span className="overflow-hidden text-ellipsis">
          {label ?? children}
        </span>
        {onDelete && (
          <button
            type="button"
            aria-label="Delete"
            onClick={(e) => {
              e.stopPropagation()
              onDelete(e)
            }}
            className="-mr-1 flex shrink-0 cursor-pointer items-center rounded-full opacity-60 transition-opacity hover:opacity-100"
          >
            {deleteIcon ?? (
              <svg viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4">
                <path d="M12 2C6.47 2 2 6.47 2 12s4.47 10 10 10 10-4.47 10-10S17.53 2 12 2zm5 13.59L15.59 17 12 13.41 8.41 17 7 15.59 10.59 12 7 8.41 8.41 7 12 10.59 15.59 7 17 8.41 13.41 12 17 15.59z" />
              </svg>
            )}
          </button>
        )}
      </Component>
    )
  },
)
Chip.displayName = 'Chip'

export default Chip
